import { useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomCellRendererProps } from 'ag-grid-react';
import { CellEditRequestEvent, ColDef, ColumnResizedEvent, ValueFormatterParams } from 'ag-grid-community';
import { InfniteScrollingGrid } from '../../AgGrid/InfiniteScrollingGrid';
import {
  getFeatureLineSetting,
  searchFeatureLineSettings,
  searchPropertyMappingsForFeatureLines,
} from '../../../api/arcgis-civil3d-feature-line-setting.api';
import { useSearchLineStyles } from '../../../hooks/api/use-line-styles.hook';
import {
  useUpdateFeatureLineSetting,
  useDeleteFeatureLineSettings,
  useDuplicateFeatureLineSettings,
  useCreatePropertyMappingsForFeatureLines,
} from '../../../hooks/api/use-arcgis-civil3d-feature-line-settings.hook';
import { civil3dFeatureTypesByCategory, Civil3dFeatureType } from '../../../../../types/constants/civil3d-feature-type';
import { ArrayFilter } from '../../AgGrid/ArrayFilter';
import {
  ArcgisCivil3dFeatureLineSettingApiModel,
  ArcgisCivil3dFeatureLineSettingRelationship,
  WithLineStyle,
} from '../../../../../types/models/arcgis-civil3d-feature-line-setting.model';
import { ArcgisCivil3dConfigurationGrid } from '../ArcgisCivil3dConfigurationGrid';
import {
  WithArcgisFeatureClass,
  WithAutodeskLayer,
  WithConfiguration,
} from '../../../../../types/models/arcgis-civil3d-setting.model';

export const FeatureLineConfigurationGrid = () => {
  const navigate = useNavigate();
  const { configurationSettingId } = useParams<{ configurationSettingId: string }>();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const [updateFeatureLineSetting, { error: updateError }] = useUpdateFeatureLineSetting();
  const [deleteFeatureLineSettings, { error: deleteError }] = useDeleteFeatureLineSettings();
  const [duplicateFeatureLineSettings, { error: duplicateError }] = useDuplicateFeatureLineSettings();
  const [createPropertyMappings, { error: createPropertyMappingsError }] = useCreatePropertyMappingsForFeatureLines();

  const { loading: loadingLineStyles, response: lineStylesResponse } = useSearchLineStyles({
    limit: 999999,
    settingConfigurationId: configurationSettingId ? Number(configurationSettingId) : undefined,
  });

  const lineStyleIdNameMap = lineStylesResponse?.pages
    .flatMap(({ result }) => result)
    .reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {} as Record<string, string>);

  const columns: ColDef<
    ArcgisCivil3dFeatureLineSettingApiModel &
      WithConfiguration &
      WithArcgisFeatureClass &
      WithAutodeskLayer &
      WithLineStyle
  >[] = [
    {
      headerName: 'Style',
      field: 'lineStyle',
      colId: 'lineStyle.name',
      valueGetter: ({ data }) => data?.lineStyle?.id,
      editable: true,
      sortable: true,
      flex: 0,
      width: columnWidths['lineStyle.name'] ? Number(columnWidths['lineStyle.name']) : 200,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: lineStylesResponse?.pages.flatMap(({ result }) => result).map(({ id }) => id) ?? [],
        formatValue: (value: number) => lineStyleIdNameMap?.[value] ?? '',
      },
      valueFormatter: (params: ValueFormatterParams<ArcgisCivil3dFeatureLineSettingApiModel>) =>
        lineStyleIdNameMap?.[params.value] ?? '',
    },
    {
      headerName: 'Site',
      colId: 'site',
      flex: 0,
      width: columnWidths.site ? Number(columnWidths.site) : 200,
      valueGetter: () => '',
    },
    {
      headerName: 'Feature Type',
      field: 'civil3dFeatureType',
      colId: 'civil3dFeatureType.name',
      width: columnWidths['civil3dFeatureType.name'] ? Number(columnWidths['civil3dFeatureType.name']) : 200,
      flex: 0,
      valueGetter: ({ data }: { data?: ArcgisCivil3dFeatureLineSettingApiModel }) => data?.civil3dFeatureType,
      filter: ArrayFilter,
      editable: true,
      sortable: true,
      filterParams: { values: civil3dFeatureTypesByCategory[Civil3dFeatureType.FEATURE_LINE] },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: civil3dFeatureTypesByCategory[Civil3dFeatureType.FEATURE_LINE],
      },
    },
    {
      headerName: 'Property Mappings',
      width: columnWidths.propertyMappings ? Number(columnWidths.propertyMappings) : 250,
      flex: 0,
      cellRenderer: ({ node }: CustomCellRendererProps<ArcgisCivil3dFeatureLineSettingApiModel>) => (
        <Button variant="text" onClick={() => navigate(`${node.id}/property-mappings`)} sx={{ textTransform: 'none' }}>
          View property mapping
        </Button>
      ),
    },
  ];

  const onColumnResized = (params: ColumnResizedEvent) => {
    setColumnWidths((prevColumnWidths) =>
      params.column
        ? {
            ...(prevColumnWidths ?? {}),
            [params.column.getColId()]: params.column.getActualWidth(),
          }
        : prevColumnWidths,
    );
  };

  const getFeatureLineSettings: Parameters<
    typeof InfniteScrollingGrid<
      ArcgisCivil3dFeatureLineSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithLineStyle
    >
  >[0]['onGetRows'] = async ({ cursor, limit, filterQuery, sortQuery }) => {
    const { data } = await searchFeatureLineSettings<
      ArcgisCivil3dFeatureLineSettingApiModel &
        WithConfiguration &
        WithArcgisFeatureClass &
        WithAutodeskLayer &
        WithLineStyle
    >({
      ...filterQuery,
      sort: sortQuery,
      cursor,
      limit,
      include: [
        ArcgisCivil3dFeatureLineSettingRelationship.AUTODESK_LAYER,
        ArcgisCivil3dFeatureLineSettingRelationship.ARCGIS_FEATURE_CLASS,
        ArcgisCivil3dFeatureLineSettingRelationship.CONFIGURATION,
        ArcgisCivil3dFeatureLineSettingRelationship.LINE_STYLE,
      ],
      configurationId: Number(configurationSettingId),
    });

    return data;
  };

  const updateSetting = async ({ colDef, data, newValue }: CellEditRequestEvent) => {
    if (!colDef?.field || !data?.id || colDef.field === 'query') return;

    const relationFieldMap = {
      autodeskLayer: 'autodeskLayerId',
      arcgisFeatureClass: 'arcgisFeatureClassId',
      lineStyle: 'lineStyleId',
    };

    const fieldKey =
      colDef.field in relationFieldMap ? relationFieldMap[colDef.field as keyof typeof relationFieldMap] : colDef.field;

    await updateFeatureLineSetting({ featureLineId: data.id, [fieldKey]: newValue });
  };

  const handleBulkDuplicatePropertyMappingsRecursive = async (
    originalDuplicateIdMap: Record<number, number>,
    idsToDuplicate: number[],
    cursor = 0,
  ) => {
    const { data } = await searchPropertyMappingsForFeatureLines({
      featureLineIds: idsToDuplicate,
      limit: 500,
      cursor,
    });

    const propertyMappingsToDuplicate = data.result.map(({ setting, attribute, civil3dProperty }) => ({
      featureLineSettingId: originalDuplicateIdMap[setting!.id],
      attributeId: attribute.id,
      civil3dPropertyId: civil3dProperty.id,
    }));
    if (propertyMappingsToDuplicate.length > 0) {
      await createPropertyMappings({ propertyMappings: propertyMappingsToDuplicate });
    }

    if (data.nextCursor) {
      await handleBulkDuplicatePropertyMappingsRecursive(originalDuplicateIdMap, idsToDuplicate, data.nextCursor);
    }
  };

  return (
    <ArcgisCivil3dConfigurationGrid
      featureType={Civil3dFeatureType.FEATURE_LINE}
      error={updateError || deleteError || duplicateError || createPropertyMappingsError}
      loading={loadingLineStyles}
      columns={columns}
      getSetting={async (featureLineId) => {
        const { data } = await getFeatureLineSetting({
          featureLineId,
          include: [
            ArcgisCivil3dFeatureLineSettingRelationship.CONFIGURATION,
            ArcgisCivil3dFeatureLineSettingRelationship.ARCGIS_FEATURE_CLASS,
          ],
        });
        return data;
      }}
      onUpdateQuery={async (featureLineId, query) => {
        await updateFeatureLineSetting({ featureLineId, query });
      }}
      onGetRows={getFeatureLineSettings}
      onUpdateRow={updateSetting}
      onColumnResized={onColumnResized}
      onBulkDelete={async (settingIds) => {
        await deleteFeatureLineSettings({ featureLineIds: settingIds });
      }}
      onBulkDuplicate={async (settingIds) => {
        const duplicatedRecords = await duplicateFeatureLineSettings({ featureLineIds: settingIds });
        await handleBulkDuplicatePropertyMappingsRecursive(
          duplicatedRecords.reduce(
            (acc, { id, originalId }) => {
              if (!originalId) return acc;
              return { ...acc, [originalId]: id };
            },
            {} as Record<number, number>,
          ),
          settingIds,
        );
      }}
    />
  );
};
